import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { type Product, type ProductCategory } from '@/lib/types';

// With a B2B link key the same hooks read the B2B market. A wrong or replaced
// key answers 404; that is final, so it is not retried.
const productsPath = (b2bKey?: string) =>
  b2bKey ? `/products/b2b/${encodeURIComponent(b2bKey)}` : '/products';

export function useProducts(b2bKey?: string) {
  return useQuery({
    queryKey: ['/products', { b2bKey }],
    queryFn: async (): Promise<Product[]> => {
      const res = await apiClient.get(productsPath(b2bKey));
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
    retry: b2bKey ? false : undefined,
  });
}

export function useProduct(slug: string | undefined, b2bKey?: string) {
  return useQuery({
    queryKey: ['/products', slug, { b2bKey }],
    queryFn: async (): Promise<Product | null> => {
      const res = await apiClient.get(`${productsPath(b2bKey)}/${slug}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to fetch product');
      return res.json();
    },
    enabled: !!slug,
  });
}

export function useProductCategories(b2bKey?: string) {
  return useQuery({
    queryKey: ['/products/categories', { b2bKey }],
    queryFn: async (): Promise<ProductCategory[]> => {
      const res = await apiClient.get(
        b2bKey ? `${productsPath(b2bKey)}/categories` : '/products/categories',
      );
      if (!res.ok) throw new Error('Failed to fetch categories');
      return res.json();
    },
    retry: b2bKey ? false : undefined,
  });
}
